/**
 * PATH: packages/bills/src/components/_payments.tsx
 * Extracted from BillPoPanel.tsx to keep files under the 1000-line
 * ceiling. Behaviour-preserving: these were module-private helpers in
 * BillPoPanel.tsx; only the (internal) import path changed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBillsClient } from '../client';
import type {
  BillPoAllocation,
  CreatePoPaymentInput,
  PaymentMethod,
  PoPayment,
  PoPaymentStatus,
  XeroPaymentSuggestion,
} from '../types';
import {
  Field,
  inputCls,
  btnPrimary,
  btnSecondary,
  btnGhost,
} from './_primitives';


/* ─────────────────────────── Payments (PR-2) ────────────────────────── */

/**
 * Tiny coloured pill that surfaces the PO's payment state. Mirrors
 * the backend ``XeroPurchaseOrder.payment_status`` enum exactly.
 *
 * ``unpaid``        — neutral grey; no action urgency on its own.
 * ``deposit_paid``  — blue; paid > 0 but ≤ 50% (typical COD scenario).
 * ``partially_paid``— indigo; paid > 50% but < total - $0.05.
 * ``paid``          — emerald; nothing more to do here.
 * ``overpaid``      — rose; investigate (duplicate / supplier credit).
 */
export function PaymentStatusBadge({
  status,
  paid,
  total,
  currency,
}: {
  status: PoPaymentStatus;
  paid: string;
  total: string;
  currency: string;
}) {
  const palette: Record<PoPaymentStatus, string> = {
    unpaid:
      'bg-muted text-muted-foreground',
    deposit_paid:
      'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200',
    partially_paid:
      'bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200',
    paid:
      'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
    overpaid:
      'bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200',
  };
  const label: Record<PoPaymentStatus, string> = {
    unpaid: 'unpaid',
    deposit_paid: 'deposit',
    partially_paid: 'part-paid',
    paid: 'paid',
    overpaid: 'overpaid',
  };
  // Show paid/total when there's any payment progress; otherwise just
  // the status word. Keeps the badge scannable in dense layouts.
  const showAmount = status !== 'unpaid';
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${palette[status]}`}
      title={`Paid: ${currency} ${paid} of ${currency} ${total}`}
    >
      {label[status]}
      {showAmount ? ` · ${paid}/${total}` : ''}
    </span>
  );
}


/**
 * Collapsed-by-default payments mini-section nested inside a
 * LinkedPoCard. Lists existing payments + offers an inline form to
 * record a new one. The new form mirrors the look of the existing
 * CreatePoForm so the panel feels consistent.
 *
 * Loads payments lazily on first expand to keep the initial allocation
 * render cheap (the bills inbox can have a dozen+ linked POs).
 */
export function PaymentsExpander({ po }: { po: BillPoAllocation['purchase_order'] }) {
  const client = useBillsClient();
  const [open, setOpen] = useState(false);
  const [payments, setPayments] = useState<PoPayment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecord, setShowRecord] = useState(false);
  // Cached server-computed totals so the badge updates without a
  // full PO refetch after a record/delete.
  const [paidOverride, setPaidOverride] = useState<string | null>(null);
  const [statusOverride, setStatusOverride] = useState<PoPaymentStatus | null>(null);

  const refresh = useCallback(async () => {
    if (!client.listPoPayments) return;
    setLoading(true);
    setError(null);
    try {
      const r = await client.listPoPayments(po.id);
      setPayments(r.payments);
      setPaidOverride(r.amount_paid);
      setStatusOverride(r.payment_status);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [client, po.id]);

  // Lazy load on first expand only.
  useEffect(() => {
    if (open && payments === null) {
      void refresh();
    }
  }, [open, payments, refresh]);

  const handleRecord = useCallback(
    async (input: CreatePoPaymentInput) => {
      if (!client.recordPoPayment) return;
      try {
        const r = await client.recordPoPayment(po.id, input);
        setPaidOverride(r.amount_paid);
        setStatusOverride(r.payment_status as PoPaymentStatus);
        setShowRecord(false);
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to record payment');
      }
    },
    [client, po.id, refresh],
  );

  const handleDelete = useCallback(
    async (paymentId: string) => {
      if (!client.deletePoPayment) return;
      try {
        const r = await client.deletePoPayment(po.id, paymentId);
        setPaidOverride(r.amount_paid);
        setStatusOverride(r.payment_status as PoPaymentStatus);
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete payment');
      }
    },
    [client, po.id, refresh],
  );

  if (!client.listPoPayments) return null;

  const paidLabel = paidOverride ?? po.amount_paid;
  const statusLabel = statusOverride ?? po.payment_status;

  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        <span>
          Payments ({po.currency_code} {paidLabel} of {po.currency_code} {po.total}
          {' · '}
          {statusLabel})
        </span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {error && (
            <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
              {error}
            </div>
          )}
          {loading && payments === null && (
            <p className="text-[11px] italic text-muted-foreground">Loading…</p>
          )}
          {payments && payments.length === 0 && !showRecord && (
            <p className="text-[11px] italic text-muted-foreground">
              No payments recorded.
            </p>
          )}
          {payments && payments.length > 0 && (
            <PaymentsList payments={payments} onDelete={handleDelete} />
          )}
          {client.recordPoPayment && (
            <div>
              {showRecord ? (
                <RecordPaymentForm
                  poId={po.id}
                  poTotal={po.total}
                  amountPaid={paidLabel}
                  currency={po.currency_code}
                  onSubmit={handleRecord}
                  onCancel={() => setShowRecord(false)}
                />
              ) : (
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => setShowRecord(true)}
                >
                  + Record payment
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function PaymentsList({
  payments,
  onDelete,
}: {
  payments: PoPayment[];
  onDelete: (id: string) => Promise<void>;
}) {
  return (
    <ul className="space-y-1.5">
      {payments.map((p) => (
        <li
          key={p.id}
          className="rounded border border-border bg-muted/30 px-2 py-1.5 text-[11px]"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium tabular-nums">
                {p.amount}{' '}
                <span className="font-normal text-muted-foreground">
                  · {METHOD_LABELS[p.method] ?? p.method} · {p.paid_at}
                </span>
              </p>
              {p.reference && (
                <p className="truncate text-muted-foreground">
                  Ref: {p.reference}
                </p>
              )}
              {p.xero_payment_summary && (
                <p className="truncate text-emerald-700 dark:text-emerald-400">
                  ↔ Xero payment {p.xero_payment_summary.reference || p.xero_payment_summary.invoice_number || `#${p.xero_payment_summary.id}`}
                </p>
              )}
              {p.source_email_summary && (
                <p className="truncate text-muted-foreground">
                  Evidence: {p.source_email_summary.subject}
                </p>
              )}
              {p.notes && <p className="truncate">{p.notes}</p>}
              {p.created_by_name && (
                <p className="text-[10px] text-muted-foreground">
                  Recorded by {p.created_by_name}
                </p>
              )}
            </div>
            <button
              type="button"
              className={btnGhost}
              title="Remove this payment"
              onClick={() => void onDelete(p.id)}
            >
              ✕
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}


export const METHOD_LABELS: Record<PaymentMethod, string> = {
  eft: 'EFT',
  cheque: 'Cheque',
  card: 'Card',
  bpay: 'BPAY',
  cash: 'Cash',
  other: 'Other',
};


export function RecordPaymentForm({
  poId,
  poTotal,
  amountPaid,
  currency,
  onSubmit,
  onCancel,
}: {
  poId: number;
  poTotal: string;
  amountPaid: string;
  currency: string;
  onSubmit: (input: CreatePoPaymentInput) => Promise<void>;
  onCancel: () => void;
}) {
  const client = useBillsClient();
  const remaining = useMemo(() => {
    const t = parseFloat(poTotal || '0');
    const p = parseFloat(amountPaid || '0');
    const r = t - p;
    return r > 0 ? r.toFixed(2) : '0.00';
  }, [poTotal, amountPaid]);

  // Pre-fill amount with the remaining balance — most common case is
  // "I'm clearing the rest". Bookkeeper edits if it's a partial.
  const [amount, setAmount] = useState(remaining);
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<PaymentMethod>('eft');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [linkXero, setLinkXero] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Lazy-load suggestions when the bookkeeper opens the form. They're
  // cheap (max 10 rows) but not worth fetching at panel mount.
  const [suggestions, setSuggestions] = useState<XeroPaymentSuggestion[]>([]);
  useEffect(() => {
    if (!client.getPoPaymentSuggestions) return;
    let cancelled = false;
    client
      .getPoPaymentSuggestions(poId)
      .then((r) => {
        if (!cancelled) setSuggestions(r.suggestions);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, poId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return;
    setSubmitting(true);
    try {
      await onSubmit({
        amount,
        paid_at: paidAt,
        method,
        reference,
        notes,
        xero_payment: linkXero,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2 rounded border border-border bg-background p-2"
    >
      <p className="text-[11px] text-muted-foreground">
        Record a payment against PO #{poId}. Remaining balance:{' '}
        <span className="font-semibold">
          {currency} {remaining}
        </span>
        .
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Amount">
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputCls}
            required
          />
        </Field>
        <Field label="Paid on">
          <input
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            className={inputCls}
            required
          />
        </Field>
        <Field label="Method">
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            className={inputCls}
          >
            {(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => (
              <option key={m} value={m}>
                {METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference">
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Bank ref / cheque #"
            className={inputCls}
          />
        </Field>
      </div>

      {suggestions.length > 0 && (
        <Field label="Link to Xero payment (optional)">
          <select
            value={linkXero ?? ''}
            onChange={(e) =>
              setLinkXero(e.target.value ? Number(e.target.value) : null)
            }
            className={inputCls}
          >
            <option value="">— No link —</option>
            {suggestions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.date} · {s.currency_code} {s.amount}
                {s.reference ? ` · ${s.reference}` : ''}
                {s.invoice_number ? ` · inv ${s.invoice_number}` : ''}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={inputCls}
          placeholder="Anything the bookkeeper should know — supplier proof, reason, etc."
        />
      </Field>

      <div className="flex items-center justify-end gap-2">
        <button type="button" className={btnGhost} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className={btnPrimary} disabled={submitting}>
          {submitting ? 'Recording…' : 'Record payment'}
        </button>
      </div>
    </form>
  );
}
